import { useState } from 'react';
import { Link2, Upload } from 'lucide-react';
import VideoUploader from './VideoUploader';
import VideoCoverSelector from './VideoCoverSelector';
import VideoPlayer from './VideoPlayer';
import ConfirmModal from '../common/ConfirmModal';
import { toEmbedUrl, validateVideoUrl, detectVideoSourceType } from '../../utils/videoUrl';

type VideoStatus = 'uploading' | 'processing' | 'ready' | 'failed';

interface VideoInputProps {
  videoUrl?: string;
  videoId?: string;
  videoStatus?: VideoStatus;
  onChange: (videoUrl: string, videoId?: string, videoStatus?: VideoStatus) => void;
  targetType?: string;
  targetId?: string;
  className?: string;
  disabled?: boolean;
  maxSize?: number;
  onCoverSelect?: (coverUrl: string) => void;
}

type TabKey = 'link' | 'upload';

export default function VideoInput({
  videoUrl,
  videoId,
  videoStatus,
  onChange,
  targetType,
  targetId,
  className = '',
  disabled = false,
  maxSize,
  onCoverSelect,
}: VideoInputProps) {
  const [activeTab, setActiveTab] = useState<TabKey>(videoId ? 'upload' : 'link');
  const [urlInput, setUrlInput] = useState(videoUrl || '');
  const [urlError, setUrlError] = useState('');
  const [pendingTab, setPendingTab] = useState<TabKey | null>(null);

  const hasLinkContent = !!(activeTab === 'link' && urlInput.trim());
  const hasUploadContent = !!(activeTab === 'upload' && videoId);

  function handleTabSwitch(tab: TabKey) {
    if (tab === activeTab) return;

    // Show confirmation if current tab has content
    if ((hasLinkContent && tab === 'upload') || (hasUploadContent && tab === 'link')) {
      setPendingTab(tab);
      return;
    }
    setActiveTab(tab);
  }

  function confirmTabSwitch() {
    if (!pendingTab) return;

    if (activeTab === 'link') {
      setUrlInput('');
      setUrlError('');
      onChange('', undefined, undefined);
    }
    // Note: we don't auto-delete uploaded video on tab switch,
    // just switch the view. The upload stays in case they switch back.

    setActiveTab(pendingTab);
    setPendingTab(null);
  }

  function handleUrlChange(value: string) {
    setUrlInput(value);
    setUrlError('');
  }

  function handleUrlBlur() {
    const url = urlInput.trim();
    if (!url) {
      onChange('', undefined, undefined);
      return;
    }
    const err = validateVideoUrl(url);
    if (err) {
      setUrlError(err);
      return;
    }
    const embedUrl = toEmbedUrl(url);
    setUrlInput(embedUrl);
    setUrlError('');
    onChange(embedUrl, undefined, undefined);
  }

  function renderPreview() {
    const url = videoUrl || urlInput;
    if (!url) return null;

    const sourceType = detectVideoSourceType(url);
    if (sourceType === 'youtube' || sourceType === 'vimeo') {
      return (
        <div className="mt-3 aspect-video rounded-lg overflow-hidden border border-gray-200">
          <iframe
            src={url}
            title="Video preview"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    if (sourceType === 'direct') {
      return (
        <div className="mt-3">
          <VideoPlayer src={url} className="aspect-video" />
        </div>
      );
    }
    return null;
  }

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: 'link', label: 'Link', icon: <Link2 className="w-4 h-4" /> },
    { key: 'upload', label: 'Upload', icon: <Upload className="w-4 h-4" /> },
  ];

  return (
    <div className={`max-w-lg ${className}`}>
      {/* Tab headers */}
      <div className="flex border-b border-gray-200 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabSwitch(tab.key)}
            disabled={disabled}
            className={`flex items-center gap-1.5 px-4 py-2 -mb-px text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'border-b-2 border-gold text-gold'
                : 'text-gray-500 hover:text-gray-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Link tab */}
      {activeTab === 'link' && (
        <div>
          <input
            type="url"
            value={urlInput}
            onChange={(e) => handleUrlChange(e.target.value)}
            onBlur={handleUrlBlur}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold ${
              urlError ? 'border-red-500' : 'border-gray-200'
            }`}
            placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
          />
          {urlError && (
            <p className="text-sm text-red-500 mt-1">{urlError}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Supports YouTube, Vimeo, or direct video files (.mp4, .webm). URLs are auto-converted to embed format.
          </p>
          {renderPreview()}
        </div>
      )}

      {/* Upload tab */}
      {activeTab === 'upload' && (
        <div>
          {targetType && targetId ? (
            <>
              <VideoUploader
                value={videoUrl}
                videoId={videoId}
                videoStatus={videoStatus}
                onChange={(url, mediaId, status) => onChange(url, mediaId, status)}
                targetType={targetType}
                targetId={targetId}
                disabled={disabled}
                maxSize={maxSize}
              />
              {videoId && videoStatus === 'ready' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Video Cover
                  </label>
                  <VideoCoverSelector
                    mediaId={videoId}
                    onSelect={(coverUrl) => onCoverSelect?.(coverUrl)}
                    disabled={disabled}
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-sm bg-gray-50 p-3 rounded-md border border-gray-200">
              Save first to enable video upload.
            </p>
          )}
        </div>
      )}

      {/* Tab switch confirmation */}
      <ConfirmModal
        isOpen={!!pendingTab}
        onClose={() => setPendingTab(null)}
        onConfirm={confirmTabSwitch}
        title="Switch video source?"
        message={
          activeTab === 'link'
            ? 'Switching to Upload will clear the current video URL. Continue?'
            : 'Switching to Link will use a URL instead of the uploaded video. Continue?'
        }
        confirmText="Switch"
        variant="warning"
      />
    </div>
  );
}
